import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import AsyncTable, { Column } from '../../../../components/AsyncTable';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../components/Notification';
import { ActionButtonsContainer } from '../../../../components/TablePaginator/styles';
import HttpStatus from '../../../../enums/httpStatus';
import { DownloadIcon } from '../../../../icons';
import { tempFilesPaginatedApi, tempFilesTemporaryUrlApi } from '../../../../services/requests';
import ContentContainer from '../../../../templates/Content/ContentContainer';
import { getGenericRequestPageForAsyncTable } from '../../../../utils/TablesFunctions';

export type TempFile = {
  uuid: string;
  original_name?: string | null;
  size?: number | null;
  mime_type?: string | null;
  tags?: string | null;
  expires_at?: string | null;
};

const parseTags = (value?: string | null): string[] => {
  if (!value) return [];

  return value
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
};

const formatBytes = (value?: number | null): string => {
  if (!value || value <= 0) return '-';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const base = 1024;
  const exponent = Math.min(Math.floor(Math.log(value) / Math.log(base)), units.length - 1);

  const size = value / Math.pow(base, exponent);
  const formatted = exponent === 0 ? `${Math.round(size)}` : size.toFixed(2);

  return `${formatted} ${units[exponent]}`;
};

const TemporaryFilesPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [tableData, setTableData] = useState<TempFile[]>([]);
  const [updateTable] = useState(0);
  const [downloadingUuid, setDownloadingUuid] = useState<string | null>(null);

  const handleDownload = async (uuid: string) => {
    try {
      setDownloadingUuid(uuid);
      tempFilesTemporaryUrlApi.query = '';
      const response = await tempFilesTemporaryUrlApi.show(uuid);

      if (response.status !== HttpStatus.OK || !response.data?.data?.url) {
        throw response;
      }

      const url: string = response.data.data.url;

      const opened = window.open(url, '_blank', 'noopener,noreferrer');
      if (!opened) {
        window.location.assign(url);
      }
    } catch (err) {
      if (!tempFilesTemporaryUrlApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
        frontendNotification({
          message: t('errorTryingToDownloadFile'),
          type: 'error',
        });
      }
    } finally {
      setDownloadingUuid(null);
    }
  };

  const actionsButtons = (row: TempFile) => (
    <ActionButtonsContainer type={4}>
      <DownloadIcon
        title={t('download')}
        onClick={() => handleDownload(row.uuid)}
        style={{ opacity: downloadingUuid && downloadingUuid !== row.uuid ? 0.6 : 1 }}
      />
    </ActionButtonsContainer>
  );

  const tagsCell = (row: TempFile) => {
    const tags = parseTags(row.tags);
    if (tags.length === 0) return <span>-</span>;

    return (
      <div
        style={{
          display: 'flex',
          gap: '6px',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          maxWidth: '240px',
          paddingBottom: '2px',
        }}
      >
        {tags.map((tag) => (
          <span
            key={tag}
            style={{
              background: theme.colors.primary,
              color: theme.colors.onPrimary,
              padding: '2px 8px',
              borderRadius: '6px',
              fontSize: '0.85rem',
              lineHeight: '1.2rem',
              flex: '0 0 auto',
            }}
            title={tag}
          >
            {tag}
          </span>
        ))}
      </div>
    );
  };

  const expiresAtCell = (row: TempFile) => {
    if (!row.expires_at) return <span>-</span>;

    const formatted = dayjs(row.expires_at).isValid()
      ? dayjs(row.expires_at).format('DD/MM/YYYY HH:mm')
      : '-';

    return <span>{formatted}</span>;
  };

  const columns: Column<TempFile>[] = [
    {
      label: t('id') as string,
      accessor: 'uuid',
    },
    {
      label: t('originalName') as string,
      accessor: 'original_name',
    },
    {
      label: t('size') as string,
      Cell: (row) => <span>{formatBytes(row.size)}</span>,
    },
    {
      label: t('mimeType') as string,
      accessor: 'mime_type',
    },
    {
      label: t('tags') as string,
      Cell: tagsCell,
    },
    {
      label: t('expirationDate') as string,
      Cell: expiresAtCell,
    },
    {
      label: t('actions') as string,
      Cell: actionsButtons,
    },
  ];

  return (
    <ContentContainer title={t('temporaryFiles')}>
      <AsyncTable
        tableName={t('temporaryFiles')}
        columns={columns}
        value={tableData}
        onChange={setTableData}
        requestPage={getGenericRequestPageForAsyncTable(tempFilesPaginatedApi)}
        reqListener={[updateTable]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
        }}
      />
    </ContentContainer>
  );
};

export default TemporaryFilesPage;
